/**
 * PluginBridge — Host-side bridge for communicating with plugin iframes.
 *
 * Handles postMessage communication, permission enforcement,
 * rate limiting, and proxying requests to the FIAS backend.
 */

import { invokeClientEntity, getClientEntityRegistration } from '@/lib/client-entity-registry';
import { getWebSocket } from '@/lib/websocket';
import { WEBSOCKET_ENDPOINT } from '@/lib/api';
import { authApi } from '@/lib/auth-api';
import { attachCsrfHeader } from '@/lib/csrf';
// Plugin permission scopes. Imported type-only (erased at build) from the
// SDK's generated permissions — the client-safe mirror of @fias/db-types,
// generated by `pnpm generate:sdk-types` and pinned in lockstep by
// tests/architecture/sdk-permissions-match-db-types.test.ts. Importing rather
// than re-declaring the union here avoids drift: a prior hand-maintained copy
// silently fell behind db-types. A type-only import adds no runtime dependency
// on the server-only db-types package (the bridge ships in plugin iframes).
import type { PluginPermission, FiasVendoredAssets } from '@fias/arche-sdk';

/**
 * Arche id formats accepted by the `open_arche` op. The regex mirrors
 * `isValidArcheId` in `@fias/db-types` (`arche.ts`) — inlined rather than
 * imported because this bridge takes no runtime dependency on that
 * server-only package.
 */
function isValidArcheIdFormat(id: string): boolean {
  return /^arc_[a-f0-9]{32}$/.test(id) || /^arche_[a-z0-9_]+$/.test(id);
}

// Bridge message types (must match the SDK's types)
interface BridgeMessage {
  type: string;
  messageId: string;
  payload: unknown;
}

/**
 * Bridge protocol version negotiation — host side.
 *
 *   HOST_BRIDGE_PROTOCOL_VERSION
 *     The protocol version this host SPEAKS. Bumped on backward-
 *     incompatible additions to the message shape.
 *
 *   HOST_REQUIRES_SDK_PROTOCOL_VERSION
 *     Minimum SDK protocol version this host accepts. If a plugin
 *     reports a lower version in its `ready` message, the host fires
 *     `onIncompatibleSdk` so the UI can render a "plugin needs update"
 *     affordance instead of letting the plugin call ops the host has
 *     removed.
 *
 * SYNC NOTE: must rise in lockstep with
 * packages/arche-sdk/src/protocol.ts when the protocol changes.
 */
const HOST_BRIDGE_PROTOCOL_VERSION = 1;
const HOST_REQUIRES_SDK_PROTOCOL_VERSION = 1;

/**
 * Max time the host waits for a plugin to return its state snapshot after a
 * `preview_state_capture` request. Plugins that support the protocol reply
 * within a tick (the SDK handler is synchronous), so this only bounds the
 * pathological case; it never delays plugins that don't advertise support
 * (those skip capture entirely).
 */
const PREVIEW_STATE_CAPTURE_TIMEOUT_MS = 250;

/**
 * Cap on the serialized size of a captured preview-state snapshot. Mirrors the
 * defensive posture of the preview storage budget — a buggy plugin returning a
 * huge object shouldn't be ferried across the reload. Oversized snapshots are
 * dropped (logged), so the worst case is a non-restored report, not an OOM.
 */
const PREVIEW_STATE_MAX_BYTES = 512 * 1024;

interface PluginBridgeOptions {
  archId: string;
  permissions: PluginPermission[];
  iframe: HTMLIFrameElement;
  onResize?: (height: number) => void;
  onToast?: (message: string, variant: string) => void;
  onNavigate?: (path: string) => void;
  /**
   * Cross-arche navigation requested via the `open_arche` op. `archeId` is
   * already format-validated by the bridge; the host resolves it to
   * `/a/<archeId>` and navigates (same-tab) or, when `newTab` is set,
   * best-effort opens a new tab.
   */
  onOpenArche?: (archeId: string, newTab: boolean) => void;
  getTheme: () => unknown;
  /** Self-hosted vendored-library asset URLs (keyed by import specifier) for the
   *  init payload. Called only when the plugin declares
   *  `sandbox:vendored-libraries`; returns null when the CDN is unconfigured. */
  getVendoredAssets?: () => FiasVendoredAssets | null;
  getUser: () => Promise<{ userId: string; displayName: string; avatar: string | null }>;
  bridgeApiUrl: string;
  getAuthToken: () => Promise<string>;
  /** When true, uses local mocks for storage and custom handler for entity invoke. */
  previewMode?: boolean;
  /** Custom entity_invoke handler for preview (avoids backend proxy for entity lookup). */
  onEntityInvoke?: (payload: {
    entityId: string;
    input: string;
    systemPrompt?: string;
  }) => Promise<{ output: string }>;
  /** Custom image_generate handler for preview (avoids backend proxy for image config lookup). */
  onImageGenerate?: (payload: {
    entityId: string;
    prompt: string;
    size?: string;
    quality?: string;
    style?: string;
  }) => Promise<{
    imageUrl: string;
    imageRef: string;
    revisedPrompt?: string;
    provider: string;
    model: string;
  }>;
  /** External storage map for preview mode — survives bridge rebuilds when passed from parent. */
  previewStorage?: Map<string, string>;
  /** Custom storage handler for preview mode — when provided, proxies to backend instead of in-memory. */
  onStorage?: (action: string, params: Record<string, unknown>) => Promise<unknown>;
  /** Callback for bridge-level log events (e.g., burst detection warnings). */
  onLog?: (level: 'warn' | 'error', message: string) => void;
  /**
   * Fired when the SDK acknowledges init with a `sdkProtocolVersion`
   * lower than `HOST_REQUIRES_SDK_PROTOCOL_VERSION`. The host UI should
   * unmount the iframe and render a "plugin needs update" affordance.
   * Receives the actual SDK version the plugin reported.
   */
  onIncompatibleSdk?: (sdkProtocolVersion: number, minRequired: number) => void;
  /**
   * Callback for IAP purchase requests. When set, store_purchase messages
   * invoke this instead of proxying directly to backend. The callback should
   * show a confirmation UI and resolve with the backend response on confirm,
   * or { success: false, error: 'USER_CANCELED' } on cancel.
   */
  onPurchaseRequest?: (product: {
    productIdentifier: string;
    name: string;
    description: string | null;
    priceCredits: number;
    type: string;
  }) => Promise<unknown>;
  /** Mock store products for preview mode. */
  previewStoreProducts?: Array<{
    productIdentifier: string;
    type: string;
    name: string;
    description: string | null;
    priceCredits: number;
    metadata: Record<string, unknown>;
    subscriptionPeriod?: string;
  }>;
  /**
   * Preview-mode only. A plugin state snapshot captured from the previous
   * iframe just before this (re)load — see `captureState()`. When the freshly
   * loaded plugin signals readiness and advertises preview-state support, the
   * bridge replays this snapshot so transient UI state (e.g. a generated
   * report) survives a rebuild. Ignored outside preview mode.
   */
  initialPreviewState?: unknown;
  /** Fired after `initialPreviewState` has been sent to the plugin for restore. */
  onPreviewStateRestored?: () => void;
}

/**
 * Rate limiting state per message type.
 */
interface RateLimitBucket {
  count: number;
  windowStart: number;
}

const RATE_LIMITS: Record<string, { maxPerMinute: number }> = {
  // Backstop against popup-bombing — a plugin that requests newTab opens
  // could otherwise spawn tabs in a loop. Tile clicks are well under this.
  open_arche: { maxPerMinute: 30 },
  entity_invoke: { maxPerMinute: 60 },
  client_entity_invoke: { maxPerMinute: 60 },
  image_generate: { maxPerMinute: 10 },
  audio_generate: { maxPerMinute: 10 },
  storage_write: { maxPerMinute: 120 },
  storage_read: { maxPerMinute: 300 },
  storage_list: { maxPerMinute: 60 },
  storage_delete: { maxPerMinute: 60 },
  data_put: { maxPerMinute: 120 },
  data_get: { maxPerMinute: 300 },
  data_query: { maxPerMinute: 60 },
  data_delete: { maxPerMinute: 60 },
  data_batch: { maxPerMinute: 30 },
  data_create_collection: { maxPerMinute: 10 },
  data_delete_collection: { maxPerMinute: 10 },
  data_list_collections: { maxPerMinute: 60 },
  // Workspace tenancy (ADR-010) — mirrors server-side DATA_STORE_RATE_LIMITS.
  workspace_create: { maxPerMinute: 20 },
  workspace_list: { maxPerMinute: 120 },
  workspace_get: { maxPerMinute: 120 },
  workspace_archive: { maxPerMinute: 20 },
  workspace_list_members: { maxPerMinute: 120 },
  workspace_add_member: { maxPerMinute: 30 },
  workspace_update_member: { maxPerMinute: 30 },
  workspace_remove_member: { maxPerMinute: 30 },
  store_get_products: { maxPerMinute: 60 },
  store_purchase: { maxPerMinute: 10 },
  store_get_entitlements: { maxPerMinute: 60 },
  store_get_purchase_history: { maxPerMinute: 30 },
  store_restore: { maxPerMinute: 10 },
  store_cancel_subscription: { maxPerMinute: 10 },
  vault_documents_list: { maxPerMinute: 120 },
  vault_documents_read: { maxPerMinute: 300 },
  // Batch reads count as ONE bridge call. Per-doc rate-limiting on the
  // batch payload (max 50 ids per call) is enforced server-side.
  vault_documents_read_many: { maxPerMinute: 60 },
  vault_documents_download_url: { maxPerMinute: 300 },
  vault_documents_get_urls: { maxPerMinute: 60 },
  vault_documents_write: { maxPerMinute: 60 },
  // Composite binary upload (init→PUT→finalize host-side) — heavier than a
  // text write, so a tighter cap. Backend quota still applies (defense-in-depth).
  vault_documents_upload: { maxPerMinute: 30 },
  vault_documents_update: { maxPerMinute: 60 },
  vault_documents_delete: { maxPerMinute: 30 },
  arche_assets_list: { maxPerMinute: 600 },
  arche_assets_index: { maxPerMinute: 120 },
  arche_assets_get_url: { maxPerMinute: 300 },
};

/** Burst detection: catches runaway loops (e.g., useEffect calling dataStore.put every render). */
interface BurstBucket {
  count: number;
  windowStart: number;
  /** When set, all calls of this type are blocked until this timestamp. */
  blockedUntil?: number;
}

const BURST_THRESHOLD = 50;
const BURST_WINDOW_MS = 5_000;
const BURST_COOLDOWN_MS = 10_000;

/** Deterministic caller identity for preview-mode workspace tenancy (ADR-010). */
const PREVIEW_USER_ID = 'preview-user';

/**
 * Extracts a short diagnostic identifier from a bridge call's payload so that
 * rate-limit and runaway-loop errors can point to the specific path/resource
 * being hammered (e.g. `__state/gameState` when a game loop is spamming
 * `usePersistentState` writes). Returns an empty string for call types where
 * no sensible target exists.
 */
function describeBridgeTarget(type: string, payload: unknown): string {
  if (!payload || typeof payload !== 'object') return '';
  const p = payload as Record<string, unknown>;

  switch (type) {
    case 'storage_read':
    case 'storage_write':
    case 'storage_delete':
      return typeof p.path === 'string' ? `path: ${p.path}` : '';
    case 'storage_list':
      return typeof p.prefix === 'string' ? `prefix: ${p.prefix}` : '';
    case 'data_put':
    case 'data_get':
    case 'data_delete': {
      const collection = typeof p.collection === 'string' ? p.collection : '';
      const docId = typeof p.docId === 'string' ? p.docId : '';
      if (collection && docId) return `${collection}/${docId}`;
      return collection ? `collection: ${collection}` : '';
    }
    case 'data_query':
    case 'data_create_collection':
    case 'data_delete_collection':
      return typeof p.collection === 'string' ? `collection: ${p.collection}` : '';
    case 'entity_invoke':
    case 'client_entity_invoke':
      return typeof p.entityId === 'string' ? `entity: ${p.entityId}` : '';
    case 'navigate':
      return typeof p.path === 'string' ? `path: ${p.path}` : '';
    case 'vault_documents_read':
    case 'vault_documents_download_url':
    case 'vault_documents_update':
    case 'vault_documents_delete':
      return typeof p.documentId === 'string' ? `documentId: ${p.documentId}` : '';
    case 'vault_documents_write':
      return typeof p.name === 'string' ? `name: ${p.name}` : '';
    case 'vault_documents_list':
      return typeof p.search === 'string' ? `search: ${p.search}` : '';
    default:
      return '';
  }
}

// Thrown by checkBurst when a call is blocked for runaway-loop reasons. The
// burst path already fires onLog on the first breach and deliberately stays
// silent during cooldown — the outer request-failure catch uses this tag to
// skip its own onLog so we don't double-log the breach and re-log every
// cooldown-blocked call.
class BridgeBurstError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'BridgeBurstError';
  }
}

// Preview-mode storage caps. Preview mode is explicitly not production —
// these caps exist so a preview-mode plugin can't pin the renderer tab by
// writing unbounded state into the in-memory Maps. Production writes go
// through the backend and are bounded by the datastore quota middleware.
const PREVIEW_STORAGE_MAX_ENTRIES = 10_000;
const PREVIEW_STORAGE_MAX_TOTAL_BYTES = 5 * 1024 * 1024; // 5 MB
const PREVIEW_DATASTORE_MAX_DOCS_PER_COLLECTION = 5_000;
const PREVIEW_DATASTORE_MAX_DOC_BYTES = 256 * 1024; // 256 KB per doc

/**
 * Async-surface poll-fallback cadence (image_generate + surface_invoke).
 *
 * The WebSocket broadcast / late-subscribe replay is the primary, fast path —
 * it resolves the moment the result lands. This poll only covers the case
 * where BOTH the live broadcast was missed AND the subscription was already
 * open when the (now-recoverable) server-side completion happened, so the
 * one-shot replay never re-fires for this connection.
 *
 * Cadence: stay quiet through the typical generation window (no polls on the
 * happy path, where WS wins first), then — if still unresolved — poll on a
 * short interval so a missed broadcast recovers in seconds, not the old 180 s.
 * The server's `request_state` row makes these polls cross-replica durable.
 */
const ASYNC_POLL_GRACE_MS = 15_000; // first poll after the typical gen window
const ASYNC_POLL_INTERVAL_MS = 5_000; // then every 5 s
const ASYNC_POLL_MAX_MS = 180_000; // hard ceiling (unchanged safety bound)

/**
 * Permission requirements per bridge call type.
 */
const PERMISSION_MAP: Record<string, PluginPermission> = {
  get_user: 'user:profile:read',
  get_theme: 'theme:read',
  entity_invoke: 'entities:invoke',
  image_generate: 'entities:image_generate',
  entities_list_image: 'entities:image_generate',
  audio_generate: 'entities:audio_generate',
  // `client_entity_invoke` deliberately omitted: the required permission
  // is per-entity (resolved from `pluginInvocable.pluginPermission` at
  // request time inside the case block), so the centralized map can't
  // answer statically.
  storage_read: 'storage:sandbox',
  storage_write: 'storage:sandbox',
  storage_list: 'storage:sandbox',
  storage_delete: 'storage:sandbox',
  data_create_collection: 'data:store',
  data_list_collections: 'data:store',
  data_delete_collection: 'data:store',
  data_put: 'data:store',
  data_get: 'data:store',
  data_query: 'data:store',
  data_delete: 'data:store',
  data_batch: 'data:store',
  data_search: 'data:search',
  // Workspace tenancy (ADR-010): lifecycle + membership ops require
  // `data:workspace`. Workspace-scoped data_put/get/delete keep `data:store`
  // above and add the server-side role gate.
  workspace_create: 'data:workspace',
  workspace_list: 'data:workspace',
  workspace_get: 'data:workspace',
  workspace_archive: 'data:workspace',
  workspace_list_members: 'data:workspace',
  workspace_add_member: 'data:workspace',
  workspace_update_member: 'data:workspace',
  workspace_remove_member: 'data:workspace',
  store_get_products: 'store:purchase',
  store_purchase: 'store:purchase',
  store_get_entitlements: 'store:purchase',
  store_get_purchase_history: 'store:purchase',
  store_restore: 'store:purchase',
  store_cancel_subscription: 'store:purchase',
  vault_documents_list: 'vault:documents:read',
  vault_documents_read: 'vault:documents:read',
  vault_documents_read_many: 'vault:documents:read',
  vault_documents_download_url: 'vault:documents:read',
  vault_documents_search: 'vault:documents:read',
  vault_documents_get_urls: 'vault:documents:read',
  vault_documents_write: 'vault:documents:write',
  vault_documents_update: 'vault:documents:write',
  vault_documents_delete: 'vault:documents:write',
  vault_documents_attach: 'vault:documents:write',
  vault_documents_detach: 'vault:documents:write',
  vault_documents_upload_init: 'vault:documents:write',
  vault_documents_upload_finalize: 'vault:documents:write',
  vault_documents_upload: 'vault:documents:write',
  arche_assets_list: 'assets:read',
  arche_assets_index: 'assets:read',
  arche_assets_get_url: 'assets:read',
  open_arche: 'navigation:open_arche',
};

export class PluginBridgeHost {
  private options: PluginBridgeOptions;
  private rateLimits = new Map<string, RateLimitBucket>();
  private burstBuckets = new Map<string, BurstBucket>();
  /** In-memory storage for preview mode (no backend calls needed). */
  private previewStorage: Map<string, string>;
  /** In-memory data store for preview mode — maps collection → (key → document). */
  private previewDataStore = new Map<string, Map<string, Record<string, unknown>>>();
  /** Collection metadata for preview data store. */
  private previewCollections = new Map<
    string,
    { name: string; userScope: string; createdAt: string }
  >();
  /**
   * In-memory workspace tenancy for preview mode (ADR-010). The preview caller
   * is always the founding owner, so every membership op is exercised against a
   * deterministic owner identity (`PREVIEW_USER_ID`).
   */
  private previewWorkspaces = new Map<
    string,
    {
      workspaceId: string;
      displayName: string;
      createdAt: string;
      archivedAt: string | null;
      members: Map<string, string>; // username → role
    }
  >();

  private initRetryTimers: ReturnType<typeof setTimeout>[] = [];

  /**
   * Whether the connected plugin's SDK advertised preview-state support in its
   * `ready` message. Gates capture/restore so older SDKs (and non-preview
   * surfaces) are unaffected and pay no latency.
   */
  private pluginSupportsPreviewState = false;
  /** One-shot guard so a repeated `ready` (e.g. StrictMode) doesn't re-restore. */
  private hasRestored = false;
  /** Resolver for an in-flight captureState() awaiting the plugin's reply. */
  private pendingStateCapture: {
    resolve: (snapshot: unknown) => void;
    timer: ReturnType<typeof setTimeout>;
  } | null = null;

  /**
   * In-flight WS-async bridge requests (today: `image_generate`).
   *
   * Each entry holds the Promise resolver pair that completes the
   * outer `handleRequest` Promise, plus a cleanup callback that
   * unsubscribes the WS listeners and clears the safety timeout. Keyed
   * by the server-issued `requestId` so the WS callbacks (which fire
   * for every in-flight subscription the user owns) can filter to the
   * matching entry and ignore broadcasts for other plugins/contexts.
   *
   * Cleanup runs on EVERY terminal path — WS success, WS error,
   * safety-timeout fallback, host `destroy()` — so listeners cannot
   * leak across plugin sessions. See `plan §"Cleanup contract"`.
   */
  private pendingImageRequests = new Map<
    string,
    {
      resolve: (value: unknown) => void;
      reject: (err: Error) => void;
      cleanup: () => void;
    }
  >();

  /**
   * In-flight WS-async `surface_invoke` requests (surfaces with
   * `asyncDispatch`). Same contract as `pendingImageRequests` — keyed by
   * the server `requestId`, torn down on every terminal path.
   */
  private pendingSurfaceRequests = new Map<
    string,
    {
      resolve: (value: unknown) => void;
      reject: (err: Error) => void;
      cleanup: () => void;
    }
  >();

  constructor(options: PluginBridgeOptions) {
    this.options = options;
    this.previewStorage = options.previewStorage ?? new Map<string, string>();
    this.handleMessage = this.handleMessage.bind(this);
    window.addEventListener('message', this.handleMessage);
  }

  /**
   * Send the init message to the plugin iframe to establish the bridge.
   *
   * When loading via srcDoc, the iframe's onLoad fires before ES module
   * scripts (loaded from esm.sh) have executed. The SDK's message listener
   * may not exist yet, so we retry init a few times with increasing delays.
   * The SDK ignores duplicate init messages after the first.
   */
  init(): void {
    this.sendInit();
    this.initRetryTimers = [
      setTimeout(() => this.sendInit(), 200),
      setTimeout(() => this.sendInit(), 1000),
      setTimeout(() => this.sendInit(), 3000),
    ];
  }

  private sendInit(): void {
    this.sendToPlugin({
      type: 'init',
      messageId: 'init_0',
      payload: {
        archId: this.options.archId,
        permissions: this.options.permissions,
        theme: this.options.getTheme(),
        // Vendored-library asset URLs — only for plugins that declared the
        // capability, so ordinary plugins never see CDN internals in their init
        // payload.
        vendoredAssets:
          this.options.permissions.includes('sandbox:vendored-libraries') &&
          this.options.getVendoredAssets
            ? this.options.getVendoredAssets()
            : null,
        currentPath: window.location.pathname,
        // Protocol-version handshake (see HOST_BRIDGE_PROTOCOL_VERSION
        // block above). The SDK rejects hosts older than its required
        // minimum; the host rejects plugins older than ours via the
        // `ready` message handler below.
        hostProtocolVersion: HOST_BRIDGE_PROTOCOL_VERSION,
        minSdkProtocolVersion: HOST_REQUIRES_SDK_PROTOCOL_VERSION,
      },
    });
  }

  /**
   * Notify the plugin of a theme change.
   */
  sendThemeUpdate(): void {
    this.sendToPlugin({
      type: 'theme_update',
      messageId: `theme_${Date.now()}`,
      payload: this.options.getTheme(),
    });
  }

  /**
   * Navigate the plugin to a specific step/node.
   */
  navigateToStep(nodeId: string): void {
    this.sendToPlugin({
      type: 'step_navigate',
      messageId: `step_${Date.now()}`,
      payload: { nodeId },
    });
  }

  /**
   * Ask the running plugin for a serializable snapshot of its transient UI
   * state, so the host can restore it after a preview rebuild reloads the
   * iframe (which otherwise destroys that state — e.g. a generated report).
   *
   * Resolves with the snapshot (whatever the plugin returns) or `null` if the
   * plugin doesn't support the protocol or doesn't reply within
   * `PREVIEW_STATE_CAPTURE_TIMEOUT_MS`. Never rejects — a failed capture must
   * not block the rebuild.
   */
  captureState(): Promise<unknown> {
    if (!this.pluginSupportsPreviewState) return Promise.resolve(null);
    // A previous capture that's still pending is superseded — resolve it null.
    if (this.pendingStateCapture) {
      clearTimeout(this.pendingStateCapture.timer);
      this.pendingStateCapture.resolve(null);
      this.pendingStateCapture = null;
    }
    return new Promise<unknown>((resolve) => {
      const finish = (snapshot: unknown) => {
        if (this.pendingStateCapture) {
          clearTimeout(this.pendingStateCapture.timer);
          this.pendingStateCapture = null;
        }
        resolve(snapshot);
      };
      const timer = setTimeout(() => finish(null), PREVIEW_STATE_CAPTURE_TIMEOUT_MS);
      this.pendingStateCapture = { resolve: finish, timer };
      this.sendToPlugin({
        type: 'preview_state_capture',
        messageId: `pscap_${Date.now()}`,
        payload: {},
      });
    });
  }

  /**
   * Replay a previously-captured state snapshot to the freshly loaded plugin.
   * Called once the plugin signals readiness (so its SDK and restore handler
   * are live). No-op unless the plugin supports the protocol and an
   * `initialPreviewState` was provided.
   */
  private maybeRestoreState(): void {
    if (this.hasRestored || !this.pluginSupportsPreviewState) return;
    const snapshot = this.options.initialPreviewState;
    if (snapshot === undefined || snapshot === null) return;
    this.hasRestored = true;
    this.sendToPlugin({
      type: 'preview_state_restore',
      messageId: `psrest_${Date.now()}`,
      payload: snapshot,
    });
    this.options.onPreviewStateRestored?.();
  }

  /**
   * Clean up event listeners and release all in-memory state.
   *
   * The per-call Maps (rateLimits, burstBuckets) are bounded by the number
   * of unique message types (~20) so they don't grow unbounded on their
   * own, but preview Maps track plugin-controlled keys. Clearing them here
   * prevents re-mount loops in dev from accumulating state across hosts.
   */
  destroy(): void {
    for (const timer of this.initRetryTimers) clearTimeout(timer);
    this.initRetryTimers = [];
    // Resolve any in-flight state capture so an awaiting onBeforePreviewReload
    // doesn't hang on a torn-down bridge.
    if (this.pendingStateCapture) {
      clearTimeout(this.pendingStateCapture.timer);
      this.pendingStateCapture.resolve(null);
      this.pendingStateCapture = null;
    }
    // Tear down any in-flight WS-async bridge requests so the iframe's
    // awaiting Promises reject deterministically. The cleanup function
    // unsubs the WS listeners + clears the safety timer; the reject
    // surfaces to the outer `handleRequest` await which posts an error
    // response into the iframe.
    for (const [, entry] of this.pendingImageRequests) {
      entry.cleanup();
      entry.reject(new Error('plugin_unmounted'));
    }
    this.pendingImageRequests.clear();
    for (const [, entry] of this.pendingSurfaceRequests) {
      entry.cleanup();
      entry.reject(new Error('plugin_unmounted'));
    }
    this.pendingSurfaceRequests.clear();
    window.removeEventListener('message', this.handleMessage);
    this.rateLimits.clear();
    this.burstBuckets.clear();
    this.previewStorage.clear();
    this.previewDataStore.clear();
    this.previewCollections.clear();
    this.previewWorkspaces.clear();
  }

  /**
   * Returns the total byte size of the preview storage Map. Cheap since
   * preview storage values are always strings.
   */
  private getPreviewStorageTotalBytes(): number {
    let total = 0;
    for (const [key, value] of this.previewStorage) {
      total += key.length + value.length;
    }
    return total;
  }

  private async handleMessage(event: MessageEvent): Promise<void> {
    // Only handle messages from our plugin iframe
    if (event.source !== this.options.iframe.contentWindow) return;

    const data = event.data;
    if (!data || typeof data !== 'object' || !data.type) return;

    // Plugin SDK requesting init (handles srcDoc module loading race condition)
    if (data.type === 'request_init') {
      this.sendInit();
      return;
    }

    // All other messages require messageId
    if (!(data as BridgeMessage).messageId) return;

    // Fire-and-forget messages (no response needed)
    if (data.type === 'ready') {
      // Plugin acknowledged init — stop retrying
      for (const timer of this.initRetryTimers) clearTimeout(timer);
      this.initRetryTimers = [];

      // Protocol-version compatibility check. The SDK ≥ v1 sends its
      // `sdkProtocolVersion` in the ready payload. An older SDK omits
      // the field — that's treated as v1 (the first published contract),
      // which is forward-compatible if our minimum is also v1. Once we
      // bump HOST_REQUIRES_SDK_PROTOCOL_VERSION past 1, a missing field
      // becomes "too old" automatically.
      const payload = (data as BridgeMessage).payload as
        | { sdkProtocolVersion?: number; supportsPreviewState?: boolean }
        | undefined;
      const sdkVersion = payload?.sdkProtocolVersion ?? 1;
      if (sdkVersion < HOST_REQUIRES_SDK_PROTOCOL_VERSION) {
        this.options.onLog?.(
          'warn',
          `Plugin bridge SDK v${sdkVersion} is older than the minimum this host accepts (v${HOST_REQUIRES_SDK_PROTOCOL_VERSION}).`,
        );
        this.options.onIncompatibleSdk?.(sdkVersion, HOST_REQUIRES_SDK_PROTOCOL_VERSION);
      }
      // Preview state preservation is gated on the plugin advertising support,
      // and only meaningful in preview mode. Now that the plugin's SDK is live,
      // replay any snapshot captured before this reload.
      this.pluginSupportsPreviewState =
        this.options.previewMode === true && payload?.supportsPreviewState === true;
      this.maybeRestoreState();
      return;
    }

    // Plugin's reply to a captureState() request — its serializable snapshot.
    if (data.type === 'preview_state') {
      let snapshot = (data as BridgeMessage).payload ?? null;
      // Defensive cap: drop an oversized snapshot rather than ferry it across
      // the reload. The report just won't restore — never an OOM.
      if (snapshot !== null) {
        let bytes = 0;
        try {
          bytes = JSON.stringify(snapshot).length;
        } catch {
          // Non-serializable (shouldn't happen post-structured-clone) — drop it.
          bytes = PREVIEW_STATE_MAX_BYTES + 1;
        }
        if (bytes > PREVIEW_STATE_MAX_BYTES) {
          this.options.onLog?.(
            'warn',
            `Preview state snapshot dropped: ${bytes} bytes exceeds the ${PREVIEW_STATE_MAX_BYTES}-byte limit.`,
          );
          snapshot = null;
        }
      }
      this.pendingStateCapture?.resolve(snapshot);
      return;
    }

    if (data.type === 'resize') {
      const { height } = data.payload as { height: number };
      if (typeof height === 'number' && height > 0) {
        this.options.onResize?.(height);
      }
      return;
    }

    if (data.type === 'toast') {
      const { message, variant } = data.payload as { message: string; variant: string };
      if (typeof message === 'string') {
        this.options.onToast?.(message, variant ?? 'info');
      }
      return;
    }

    if (data.type === 'navigate') {
      const { path } = data.payload as { path: string };
      if (typeof path !== 'string' || path.length === 0) {
        const message = `Bridge navigate rejected: path must be a non-empty string (got: ${typeof path === 'string' ? '<empty>' : '<missing>'})`;
        this.options.onLog?.('error', message);
        this.sendError(data.messageId, message);
        return;
      }
      // Plugins navigate in their own URL space (e.g. `/edit/<id>` or
      // `/shared/<id>`); the platform's URL space is `/a/<archeId>/...`.
      // Auto-prepend the plugin's own arche prefix when the plugin sends
      // a root-relative path so plugin code doesn't have to know its own
      // archeId or the platform path convention. Mirrors how `currentPath`
      // is delivered inbound — both sides agree on the platform path.
      //
      // Security: we always scope to *this plugin's own* archeId. A path
      // already in `/a/<own-archeId>/...` form is passed through; one
      // pointing at a different arche is rejected so a plugin can't
      // navigate the parent window into someone else's arche space.
      const ownPrefix = `/a/${this.options.archId}`;
      let resolved: string | null = null;
      if (path === ownPrefix || path.startsWith(`${ownPrefix}/`)) {
        resolved = path;
      } else if (path.startsWith('/a/')) {
        const message = `Bridge navigate rejected: cross-arche navigation not allowed (got: ${path})`;
        this.options.onLog?.('error', message);
        this.sendError(data.messageId, message);
        return;
      } else if (path.startsWith('/')) {
        resolved = path === '/' ? ownPrefix : `${ownPrefix}${path}`;
      }
      if (!resolved) {
        const message = `Bridge navigate rejected: path must be root-relative (got: ${path})`;
        this.options.onLog?.('error', message);
        this.sendError(data.messageId, message);
        return;
      }
      this.options.onNavigate?.(resolved);
      // Acknowledge so the plugin's `await bridge.request('navigate', ...)`
      // resolves instead of timing out 30s later.
      this.sendResponse(data.messageId, null);
      return;
    }

    // Request/response messages — check permissions and rate limits
    try {
      const result = await this.handleRequest(data);
      this.sendResponse(data.messageId, result);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      // Surface the error to the preview log stream so get_preview_logs picks
      // it up. Without this the plugin gets a rejected Promise but the AI
      // has no visibility unless the plugin code explicitly console.errors.
      // BridgeBurstError is the exception: the burst path manages its own
      // onLog (fires once on breach, silent during cooldown) — re-logging
      // here would double-log the breach and drown the logs during cooldown.
      if (!(err instanceof BridgeBurstError)) {
        this.options.onLog?.('error', `Bridge request failed (${data.type}): ${message}`);
      }
      this.sendError(data.messageId, message);
    }
  }

  private async handleRequest(msg: BridgeMessage): Promise<unknown> {
    // Permission checks run in ALL modes, including preview. The builder/dev
    // preview must fail exactly as a deployed arche would when the manifest
    // omits a permission the code uses — otherwise a missing-permission bug
    // (e.g. calling useFiasUser() without `user:profile:read`) stays hidden
    // until production, where it surfaces as an infinite "loading" hang. The
    // in-memory preview backends below (storage/data/etc.) are unaffected;
    // only the capability gate is now enforced identically to production.
    const requiredPermission = PERMISSION_MAP[msg.type];
    if (requiredPermission && !this.options.permissions.includes(requiredPermission)) {
      throw new Error(`Permission denied: ${requiredPermission} not granted`);
    }

    // Rate limiting applies in all modes (protects against runaway loops in preview)
    this.checkRateLimit(msg.type, msg.payload);

    switch (msg.type) {
      case 'get_user':
        return this.options.getUser();

      case 'get_theme':
        return this.options.getTheme();

      case 'open_arche': {
        // Cross-arche navigation. The permission check above
        // (`navigation:open_arche`) and the format validation here are the
        // whole trust boundary — the host resolves the id to `/a/<archeId>`
        // and navigates itself, so the iframe sandbox never needs popups or
        // top-navigation. We never accept a free-form path: only a
        // well-formed arche id, so a plugin cannot steer the host to an
        // arbitrary route or origin.
        const payload =
          msg.payload && typeof msg.payload === 'object'
            ? (msg.payload as { archeId?: unknown; newTab?: unknown })
            : null;
        const archeId = typeof payload?.archeId === 'string' ? payload.archeId : '';
        if (!isValidArcheIdFormat(archeId)) {
          throw new Error(
            `open_arche rejected: archeId must be a valid arche id (got: ${archeId ? archeId : '<missing>'})`,
          );
        }
        this.options.onOpenArche?.(archeId, payload?.newTab === true);
        return null;
      }

      case 'entity_invoke': {
        const payload = msg.payload as {
          entityId: string;
          input: string;
          stream?: boolean;
          systemPrompt?: string;
          images?: Array<{ mediaType: string; dataBase64: string }>;
        };
        if (this.options.previewMode && this.options.onEntityInvoke) {
          if (payload.stream) {
            return this.handlePreviewStreamingInvoke(msg.messageId, payload);
          }
          return this.options.onEntityInvoke(payload);
        }
        // Published plugins: handle streaming via SSE through the bridge endpoint
        if (payload.stream) {
          return this.handlePublishedStreamingInvoke(msg.messageId, payload);
        }
        return this.proxyToBackend(msg);
      }

      case 'image_generate': {
        const imgPayload = msg.payload as {
          entityId: string;
          prompt: string;
          size?: string;
          quality?: string;
          style?: string;
        };
        if (this.options.previewMode && this.options.onImageGenerate) {
          return this.options.onImageGenerate(imgPayload);
        }
        // Async-via-WebSocket path: POST returns `{ requestId, status:
        // 'pending' }` immediately; the final `ImageInvokeResult`
        // arrives over WS as an `image_complete` broadcast. Pre-refactor
        // this case awaited the full 30–90 s provider call on a single
        // HTTP request and hit the internal ALB's 60 s idle timeout. See
        // image-generate-bridge-handler.ts file header.
        return this.handleAsyncImageGenerate(msg);
      }

      case 'entities_list_image': {
        // Discovery op backing the SDK's `useImageEntities` hook. Pure
        // registry read on the server — no preview-mode hook needed
        // (preview can call the live registry and stays in sync with
        // production).
        return this.proxyToBackend(msg);
      }

      case 'client_entity_invoke': {
        // Generic dispatcher for `executionMode: 'client-side'` entities.
        // Resolves the entity registration by ID (from the client-side
        // registry — populated by executor module imports at startup),
        // gates on the entity's declared `pluginPermission`, and hands off
        // to the registry. No per-entity case needed: adding a new
        // client-side entity is an entity-config + executor change, never
        // a bridge change.
        const payload =
          msg.payload && typeof msg.payload === 'object'
            ? (msg.payload as { entityId?: unknown; input?: unknown })
            : null;
        if (!payload || typeof payload.entityId !== 'string') {
          throw new Error('client_entity_invoke: payload.entityId (string) is required');
        }
        const registration = getClientEntityRegistration(payload.entityId);
        if (!registration) {
          throw new Error(
            `client_entity_invoke: no client-side executor registered for ${payload.entityId}`,
          );
        }
        // Per-entity permission (resolved from the entity's registration).
        // Enforced in all modes, including preview — parity with production
        // and with the centralized check in handleRequest above.
        const requiredPermission = registration.pluginPermission as PluginPermission;
        if (!this.options.permissions.includes(requiredPermission)) {
          throw new Error(`Permission denied: ${requiredPermission} not granted`);
        }
        const user = await this.options.getUser();
        return invokeClientEntity(payload.entityId, payload.input, {
          userId: user.userId,
          archeId: this.options.archId,
        });
      }

      case 'audio_generate': {
        // Audio generation runs through the server-side `audio_generate`
        // bridge op. Preview-mode hooks (parallel to onImageGenerate) can
        // be added when a preview-side mock is needed; for v1 we proxy
        // straight to the backend, which dispatches to the music model.
        return this.proxyToBackend(msg);
      }

      case 'storage_read': {
        if (this.options.previewMode) {
          if (this.options.onStorage) {
            const { path } = msg.payload as { path?: string };
            return this.options.onStorage('read', { path });
          }
          const { path } = msg.payload as { path?: string };
          const content = this.previewStorage.get(path ?? '') ?? null;
          return { content, exists: content !== null };
        }
        return this.proxyToBackend(msg);
      }

      case 'storage_write': {
        if (this.options.previewMode) {
          if (this.options.onStorage) {
            const { path, content } = msg.payload as { path?: string; content?: string };
            return this.options.onStorage('write', { path, content });
          }
          const { path, content } = msg.payload as { path?: string; content?: string };
          if (path && content !== undefined) {
            // Enforce preview storage caps so a preview plugin can't pin
            // the renderer tab with unbounded writes.
            const isNewEntry = !this.previewStorage.has(path);
            if (isNewEntry && this.previewStorage.size >= PREVIEW_STORAGE_MAX_ENTRIES) {
              throw new Error(
                `Preview storage entry limit exceeded (${PREVIEW_STORAGE_MAX_ENTRIES} entries).`,
              );
            }
            const priorBytes = isNewEntry
              ? 0
              : path.length + (this.previewStorage.get(path)?.length ?? 0);
            const newBytes = path.length + content.length;
            if (
              this.getPreviewStorageTotalBytes() - priorBytes + newBytes >
              PREVIEW_STORAGE_MAX_TOTAL_BYTES
            ) {
              throw new Error(
                `Preview storage size limit exceeded (${PREVIEW_STORAGE_MAX_TOTAL_BYTES} bytes).`,
              );
            }
            this.previewStorage.set(path, content);
          }
          return { success: true };
        }
        return this.proxyToBackend(msg);
      }

      case 'storage_list': {
        if (this.options.previewMode) {
          if (this.options.onStorage) {
            const { prefix } = msg.payload as { prefix?: string };
            return this.options.onStorage('list', { prefix });
          }
          const { prefix } = msg.payload as { prefix?: string };
          const files = [...this.previewStorage.keys()].filter(
            (k) => !prefix || k.startsWith(prefix),
          );
          return { files };
        }
        return this.proxyToBackend(msg);
      }

      case 'storage_delete': {
        if (this.options.previewMode) {
          if (this.options.onStorage) {
            const { path } = msg.payload as { path?: string };
            return this.options.onStorage('delete', { path });
          }
          const { path } = msg.payload as { path?: string };
          if (path) this.previewStorage.delete(path);
          return { success: true };
        }
        return this.proxyToBackend(msg);
      }

      // ── Data Store (in-memory for preview mode) ─────────────────────

      case 'data_create_collection': {
        if (this.options.previewMode) {
          const { name, userScope } = msg.payload as { name: string; userScope?: string };
          if (!this.previewDataStore.has(name)) {
            this.previewDataStore.set(name, new Map());
          }
          const meta = {
            name,
            userScope: userScope ?? 'user',
            createdAt: new Date().toISOString(),
          };
          this.previewCollections.set(name, meta);
          return { collection: meta };
        }
        return this.proxyToBackend(msg);
      }

      case 'data_list_collections': {
        if (this.options.previewMode) {
          return { collections: [...this.previewCollections.values()] };
        }
        return this.proxyToBackend(msg);
      }

      case 'data_delete_collection': {
        if (this.options.previewMode) {
          const { name } = msg.payload as { name: string };
          this.previewDataStore.delete(name);
          this.previewCollections.delete(name);
          return { success: true };
        }
        return this.proxyToBackend(msg);
      }

      case 'data_put': {
        if (this.options.previewMode) {
          const { collection, key, data } = msg.payload as {
            collection: string;
            key: string;
            data: Record<string, unknown>;
          };
          // Enforce preview data store caps — per-doc size + per-collection
          // entry count. Blocks unbounded writes in preview mode.
          const serialized = JSON.stringify(data);
          if (serialized.length > PREVIEW_DATASTORE_MAX_DOC_BYTES) {
            throw new Error(
              `Preview data store document size exceeds limit (${PREVIEW_DATASTORE_MAX_DOC_BYTES} bytes).`,
            );
          }
          let col = this.previewDataStore.get(collection);
          if (!col) {
            // Auto-create collection on first put
            col = new Map();
            this.previewDataStore.set(collection, col);
            this.previewCollections.set(collection, {
              name: collection,
              userScope: 'user',
              createdAt: new Date().toISOString(),
            });
          }
          if (!col.has(key) && col.size >= PREVIEW_DATASTORE_MAX_DOCS_PER_COLLECTION) {
            throw new Error(
              `Preview data store collection "${collection}" exceeds document limit (${PREVIEW_DATASTORE_MAX_DOCS_PER_COLLECTION}).`,
            );
          }
          col.set(key, { ...data, _key: key, _updatedAt: new Date().toISOString() });
          return { success: true };
        }
        return this.proxyToBackend(msg);
      }

      case 'data_get': {
        if (this.options.previewMode) {
          const { collection, key } = msg.payload as { collection: string; key: string };
          const doc = this.previewDataStore.get(collection)?.get(key) ?? null;
          return { data: doc };
        }
        return this.proxyToBackend(msg);
      }

      case 'data_query': {
        if (this.options.previewMode) {
          const { collection, limit, orderBy } = msg.payload as {
            collection: string;
            filters?: Array<{ field: string; op: string; value: unknown }>;
            orderBy?: { field: string; direction: 'asc' | 'desc' };
            limit?: number;
          };
          const col = this.previewDataStore.get(collection);
          if (!col) return { documents: [], nextCursor: null };
          let docs = [...col.entries()].map(([k, v]) => ({
            key: k,
            data: v,
            createdAt: (v._updatedAt as string) ?? new Date().toISOString(),
            updatedAt: (v._updatedAt as string) ?? new Date().toISOString(),
          }));
          if (orderBy) {
            docs.sort((a, b) => {
              const aVal = a.data[orderBy.field];
              const bVal = b.data[orderBy.field];
              if (aVal === bVal) return 0;
              const cmp = aVal != null && bVal != null && aVal < bVal ? -1 : 1;
              return orderBy.direction === 'desc' ? -cmp : cmp;
            });
          }
          if (limit) docs = docs.slice(0, limit);
          return { documents: docs, nextCursor: null };
        }
        return this.proxyToBackend(msg);
      }

      case 'data_delete': {
        if (this.options.previewMode) {
          const { collection, key } = msg.payload as { collection: string; key: string };
          this.previewDataStore.get(collection)?.delete(key);
          return { success: true };
        }
        return this.proxyToBackend(msg);
      }

      case 'data_batch': {
        if (this.options.previewMode) {
          const { operations } = msg.payload as {
            operations: Array<{
              op: 'put' | 'delete';
              collection: string;
              key: string;
              data?: Record<string, unknown>;
            }>;
          };
          // Validate every put's size BEFORE mutating, so the preview batch is
          // all-or-nothing like the live transaction (a throw applies nothing).
          for (const o of operations) {
            if (
              o.op === 'put' &&
              JSON.stringify(o.data ?? {}).length > PREVIEW_DATASTORE_MAX_DOC_BYTES
            ) {
              throw new Error(
                `Preview data store document size exceeds limit (${PREVIEW_DATASTORE_MAX_DOC_BYTES} bytes).`,
              );
            }
          }
          for (const o of operations) {
            if (o.op === 'put') {
              let col = this.previewDataStore.get(o.collection);
              if (!col) {
                col = new Map();
                this.previewDataStore.set(o.collection, col);
              }
              col.set(o.key, {
                ...(o.data ?? {}),
                _key: o.key,
                _updatedAt: new Date().toISOString(),
              });
            } else {
              this.previewDataStore.get(o.collection)?.delete(o.key);
            }
          }
          return { count: operations.length };
        }
        return this.proxyToBackend(msg);
      }

      case 'data_search': {
        // No in-browser embedder, so semantic search returns empty in preview
        // (mirrors vault_documents_search). Live mode proxies to the backend.
        if (this.options.previewMode) {
          return { matches: [] };
        }
        return this.proxyToBackend(msg);
      }

      // ── Workspace tenancy (ADR-010) ─────────────────────────────────
      // Live mode proxies to the backend (role/owner invariants enforced
      // server-side). Preview mode keeps an in-memory tenancy where the caller
      // is the founding owner — permissive (no role-ceiling) so the builder can
      // exercise every op without a real second user.

      case 'workspace_create': {
        if (this.options.previewMode) {
          const { displayName } = msg.payload as { displayName: string };
          const workspaceId = `ws_preview_${crypto.randomUUID()}`;
          const createdAt = new Date().toISOString();
          this.previewWorkspaces.set(workspaceId, {
            workspaceId,
            displayName,
            createdAt,
            archivedAt: null,
            members: new Map([[PREVIEW_USER_ID, 'owner']]),
          });
          return {
            workspace: { workspaceId, displayName, role: 'owner', createdAt, archivedAt: null },
          };
        }
        return this.proxyToBackend(msg);
      }

      case 'workspace_list': {
        if (this.options.previewMode) {
          return {
            workspaces: [...this.previewWorkspaces.values()]
              .filter((w) => w.archivedAt === null && w.members.has(PREVIEW_USER_ID))
              .map((w) => ({
                workspaceId: w.workspaceId,
                displayName: w.displayName,
                role: w.members.get(PREVIEW_USER_ID),
                createdAt: w.createdAt,
                archivedAt: w.archivedAt,
              })),
          };
        }
        return this.proxyToBackend(msg);
      }

      case 'workspace_get': {
        if (this.options.previewMode) {
          const { workspaceId } = msg.payload as { workspaceId: string };
          const w = this.previewWorkspaces.get(workspaceId);
          if (!w || w.archivedAt !== null || !w.members.has(PREVIEW_USER_ID)) {
            throw new Error(`Workspace "${workspaceId}" not found`);
          }
          return {
            workspace: {
              workspaceId: w.workspaceId,
              displayName: w.displayName,
              role: w.members.get(PREVIEW_USER_ID),
              createdAt: w.createdAt,
              archivedAt: w.archivedAt,
            },
          };
        }
        return this.proxyToBackend(msg);
      }

      case 'workspace_archive': {
        if (this.options.previewMode) {
          const { workspaceId } = msg.payload as { workspaceId: string };
          const w = this.previewWorkspaces.get(workspaceId);
          if (w) w.archivedAt = new Date().toISOString();
          return { success: true };
        }
        return this.proxyToBackend(msg);
      }

      case 'workspace_list_members': {
        if (this.options.previewMode) {
          const { workspaceId } = msg.payload as { workspaceId: string };
          const w = this.previewWorkspaces.get(workspaceId);
          return {
            members: w
              ? [...w.members.entries()].map(([username, role]) => ({
                  username,
                  role,
                  grantedAt: w.createdAt,
                  expiresAt: null,
                }))
              : [],
          };
        }
        return this.proxyToBackend(msg);
      }

      case 'workspace_add_member':
      case 'workspace_update_member': {
        if (this.options.previewMode) {
          // Preview keys members by username directly (no userId resolution
          // in the builder sandbox).
          const { workspaceId, username, role, expiresAt } = msg.payload as {
            workspaceId: string;
            username: string;
            role: string;
            expiresAt?: string;
          };
          const w = this.previewWorkspaces.get(workspaceId);
          if (w) w.members.set(username, role);
          return {
            member: {
              username,
              role,
              grantedAt: new Date().toISOString(),
              expiresAt: expiresAt ?? null,
            },
          };
        }
        return this.proxyToBackend(msg);
      }

      case 'workspace_remove_member': {
        if (this.options.previewMode) {
          const { workspaceId, username } = msg.payload as {
            workspaceId: string;
            username: string;
          };
          this.previewWorkspaces.get(workspaceId)?.members.delete(username);
          return { success: true };
        }
        return this.proxyToBackend(msg);
      }

      // ── Store / IAP ────────────────────────────────────────────────

      case 'store_get_products': {
        if (this.options.previewMode) {
          return this.options.previewStoreProducts ?? [];
        }
        return this.proxyToBackend(msg);
      }

      case 'store_purchase': {
        if (this.options.previewMode) {
          // In preview, simulate a successful purchase
          const { productIdentifier } = msg.payload as { productIdentifier: string };
          return {
            success: true,
            purchaseId: `preview_apc_${Date.now()}`,
            product: { productIdentifier, productType: 'consumable', name: productIdentifier },
          };
        }
        // Published mode: delegate to purchase confirmation callback if available
        if (this.options.onPurchaseRequest) {
          const { productIdentifier } = msg.payload as { productIdentifier: string };
          // Look up product info from backend first
          const products = (await this.proxyToBackend({
            ...msg,
            type: 'store_get_products' as BridgeMessage['type'],
            payload: {},
          })) as Array<{
            productIdentifier: string;
            name: string;
            description: string | null;
            priceCredits: number;
            type: string;
          }>;
          const product = Array.isArray(products)
            ? products.find((p) => p.productIdentifier === productIdentifier)
            : undefined;
          if (!product) {
            return { success: false, error: 'Product not found' };
          }
          // Delegate to confirmation UI
          return this.options.onPurchaseRequest(product);
        }
        // Fallback: proxy directly (no confirmation overlay)
        return this.proxyToBackend(msg);
      }

      case 'store_get_entitlements':
      case 'store_get_purchase_history':
      case 'store_restore':
      case 'store_cancel_subscription': {
        if (this.options.previewMode) {
          // In preview, return empty results
          if (msg.type === 'store_get_entitlements' || msg.type === 'store_restore') return [];
          if (msg.type === 'store_get_purchase_history') return { purchases: [] };
          return { success: true };
        }
        return this.proxyToBackend(msg);
      }

      // ── Vault Documents (Phase 12 substrate, scoped to source_arche_id) ──
      // The server-side bridge handler enforces source_arche_id == this arche
      // (or attachment via vault_document_references) and proxies through
      // vault-documents-operations.ts. Preview mode has no Vault store, so
      // these return empty results / synthetic values to keep render paths
      // from crashing.
      case 'vault_documents_list': {
        if (this.options.previewMode) {
          return { documents: [], nextCursor: null };
        }
        return this.proxyToBackend(msg);
      }
      case 'vault_documents_read': {
        if (this.options.previewMode) {
          return { content: '', contentType: 'text/plain' };
        }
        return this.proxyToBackend(msg);
      }
      case 'vault_documents_read_many': {
        if (this.options.previewMode) {
          return { results: [] };
        }
        return this.proxyToBackend(msg);
      }
      case 'vault_documents_download_url': {
        if (this.options.previewMode) {
          return { url: 'about:blank', expiresAt: Date.now() + 60_000 };
        }
        return this.proxyToBackend(msg);
      }
      case 'vault_documents_get_urls': {
        if (this.options.previewMode) {
          return { results: [] };
        }
        return this.proxyToBackend(msg);
      }
      case 'vault_documents_write': {
        if (this.options.previewMode) {
          return { documentId: `preview_vdoc_${Date.now()}` };
        }
        return this.proxyToBackend(msg);
      }
      case 'vault_documents_update':
      case 'vault_documents_delete': {
        if (this.options.previewMode) {
          return { success: true };
        }
        return this.proxyToBackend(msg);
      }
      case 'vault_documents_search': {
        if (this.options.previewMode) {
          return { matches: [] };
        }
        return this.proxyToBackend(msg);
      }
      case 'vault_documents_attach': {
        if (this.options.previewMode) {
          return {
            referenceId: `preview_ref_${Date.now()}`,
            attachedAt: new Date().toISOString(),
          };
        }
        return this.proxyToBackend(msg);
      }
      case 'vault_documents_detach': {
        if (this.options.previewMode) {
          return { success: true };
        }
        return this.proxyToBackend(msg);
      }
      case 'vault_documents_upload_init':
      case 'vault_documents_upload_finalize': {
        if (this.options.previewMode) {
          // The two-step upload protocol (init → PUT bytes to
          // presigned URL → finalize) needs a real PUT endpoint that
          // preview mode cannot provide. Returning a synthetic
          // uploadUrl like 'about:blank' would just produce a
          // confusing fetch failure when the SDK tries to upload.
          // Fail fast so plugin code can show a clearer message.
          throw new Error(
            `${msg.type} is not supported in preview mode (no presigned-URL endpoint). Test binary uploads in the published or dev-harness-live runtime.`,
          );
        }
        return this.proxyToBackend(msg);
      }

      case 'vault_documents_upload': {
        // Composite binary upload, host-orchestrated. The plugin iframe's
        // CSP is `connect-src 'none'`, so it CANNOT do the presigned PUT
        // itself (the SDK's old in-iframe `fetch` PUT was silently blocked
        // in published plugins). Here the HOST (parent window, no
        // connect-src restriction) runs init → PUT → finalize. The presigned
        // URL comes from the BACKEND (init), never from the iframe, so the
        // iframe cannot point the host at an arbitrary URL (no SSRF).
        if (this.options.previewMode) {
          throw new Error(
            'vault_documents_upload is not supported in preview mode (no presigned-URL endpoint). Test binary uploads in the published or dev-harness-live runtime.',
          );
        }
        return this.handleVaultUpload(msg.payload);
      }

      case 'surface_invoke': {
        // Invocation Surface Pattern dispatch. Per-surface permission,
        // rate-limit, and validation gates all read from the surface
        // metadata server-side, so there's no entry in PERMISSION_MAP
        // here (matches the production backend's gating model). Preview
        // mode has no registered surfaces — return a typed error so
        // plugin code can render a placeholder rather than crash.
        if (this.options.previewMode) {
          throw new Error('surface_invoke is not supported in preview mode');
        }
        // Async-capable: surfaces with `asyncDispatch` return `{ requestId,
        // status: 'pending' }` and complete over WebSocket (see
        // surface-async.ts). Synchronous surfaces return their result on the
        // HTTP response and this method passes it straight through.
        return this.handleAsyncSurfaceInvoke(msg);
      }

      // ── Arche Asset Library (contributor-curated published manifest) ──
      // The bridge backend resolves the manifest from the URL path's
      // archeId (server-trusted) and signs short-lived URLs against
      // the published entries. Preview mode has no manifest, so we
      // return an empty list / a synthetic URL.
      case 'arche_assets_list': {
        if (this.options.previewMode) {
          return { entries: [], nextCursor: null };
        }
        return this.proxyToBackend(msg);
      }
      case 'arche_assets_index': {
        if (this.options.previewMode) {
          return { groups: [] };
        }
        return this.proxyToBackend(msg);
      }
      case 'arche_assets_get_url': {
        if (this.options.previewMode) {
          return {
            assetId: 'preview',
            signedUrl: 'about:blank',
            expiresAt: Math.floor(Date.now() / 1000) + 60,
            contentType: 'image/png',
            width: 0,
            height: 0,
            sizeBytes: 0,
            name: null,
            altText: null,
            tags: [],
          };
        }
        return this.proxyToBackend(msg);
      }

      default:
        throw new Error(`Unknown bridge call type: ${msg.type}`);
    }
  }

  /**
   * Handle streaming entity invoke in preview mode via SSE.
   * Sends stream_token messages to the plugin as tokens arrive,
   * then returns the final result for the normal response message.
   */
  private async handlePreviewStreamingInvoke(
    messageId: string,
    payload: { entityId: string; input: string },
  ): Promise<unknown> {
    // Preview AI invoke authenticates as the user's SESSION (cookie +
    // CSRF), exactly like the non-streaming preview path
    // (api.invokePluginAI). The `/ai-invoke` route is session-auth
    // (requireAuth); a plugin-bridge capability token is the WRONG
    // credential here. Sending one — or the empty string the builder's
    // own arche (`arche_plugin_builder`, renderMode 'default') yields
    // when its bridge-token mint 400s — produced the "JWT … 3 parts"
    // 401 that surfaced to users as "AI not working". Regressed when the
    // bridge moved to capability tokens (commit 0a08f3c3); `getAuthToken`
    // stays correct for the published bridge path, just not here.
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    attachCsrfHeader('POST', headers);
    const response = await fetch(
      `${this.options.bridgeApiUrl}/arches/plugin-builder/sessions/${this.options.archId.replace('preview_', '')}/ai-invoke`,
      {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({ input: payload.input, entityId: payload.entityId, stream: true }),
      },
    );

    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: 'AI invocation failed' }));
      throw new Error(err.error?.message ?? 'AI invocation failed');
    }

    // If SSE streaming, consume the stream
    if (response.headers.get('content-type')?.includes('text/event-stream') && response.body) {
      let fullOutput = '';
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const data = line.slice(6);
          if (data === '[DONE]') continue;
          try {
            const parsed = JSON.parse(data);
            if (parsed.text) {
              fullOutput += parsed.text;
              // Forward token to plugin iframe
              this.options.iframe.contentWindow?.postMessage(
                { type: 'stream_token', messageId, text: parsed.text },
                '*',
              );
            }
            if (parsed.done && parsed.output) {
              fullOutput = parsed.output;
            }
          } catch {
            // Skip malformed SSE data
          }
        }
      }

      return { output: fullOutput };
    }

    // Non-streaming fallback
    return response.json();
  }

  /**
   * Handle streaming entity invoke for published plugins via the bridge endpoint.
   */
  private async handlePublishedStreamingInvoke(
    messageId: string,
    payload: {
      entityId: string;
      input: string;
      systemPrompt?: string;
      images?: Array<{ mediaType: string; dataBase64: string }>;
    },
  ): Promise<unknown> {
    const token = await this.options.getAuthToken();
    const response = await fetch(this.bridgeEndpoint('entity_invoke', payload), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        type: 'entity_invoke',
        payload: { ...payload, stream: true },
      }),
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: 'AI invocation failed' }));
      throw new Error(err.error?.message ?? 'AI invocation failed');
    }

    if (response.headers.get('content-type')?.includes('text/event-stream') && response.body) {
      let fullOutput = '';
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const data = line.slice(6);
          if (data === '[DONE]') continue;
          try {
            const parsed = JSON.parse(data);
            if (parsed.error) {
              throw new Error(parsed.error);
            }
            if (parsed.text) {
              fullOutput += parsed.text;
              this.options.iframe.contentWindow?.postMessage(
                { type: 'stream_token', messageId, text: parsed.text },
                '*',
              );
            }
            if (parsed.done && parsed.output) {
              fullOutput = parsed.output;
            }
          } catch (e) {
            if (e instanceof Error && e.message !== 'AI invocation failed') throw e;
          }
        }
      }

      return { output: fullOutput };
    }

    // Non-SSE fallback
    return response.json();
  }

  /**
   * Escalating poll fallback shared by the image and surface async paths.
   * Stays quiet for `ASYNC_POLL_GRACE_MS` (the WS path normally wins first),
   * then polls every `ASYNC_POLL_INTERVAL_MS` until a terminal result or the
   * `ASYNC_POLL_MAX_MS` ceiling. Returns a canceller the caller wires into its
   * `cleanup()` so a WS-delivered result (or host teardown) stops polling.
   *
   * `poll` is the per-surface single-shot status fetch (already auth-scoped on
   * the server). `onSettled` is invoked at most once with the outcome; the
   * loop guarantees no callback after the canceller runs.
   */
  private startPollFallback(
    poll: () => Promise<
      | { kind: 'pending' }
      | { kind: 'completed'; value: unknown }
      | { kind: 'failed'; code: string; message: string }
      | { kind: 'not_found' }
    >,
    onSettled: (
      outcome:
        | { type: 'completed'; value: unknown }
        | { type: 'failed'; error: Error }
        | { type: 'timeout' },
    ) => void,
  ): () => void {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const deadline = Date.now() + ASYNC_POLL_MAX_MS;

    const tick = async (): Promise<void> => {
      if (cancelled) return;
      let result: Awaited<ReturnType<typeof poll>>;
      try {
        result = await poll();
      } catch {
        result = { kind: 'pending' };
      }
      if (cancelled) return;

      if (result.kind === 'completed') {
        onSettled({ type: 'completed', value: result.value });
        return;
      }
      if (result.kind === 'failed') {
        onSettled({ type: 'failed', error: new Error(`${result.code}: ${result.message}`) });
        return;
      }
      // `not_found` means the server has no record of this requestId on any
      // replica (the durable request_state row makes this authoritative) —
      // stop rather than poll out the ceiling.
      if (result.kind === 'not_found' || Date.now() >= deadline) {
        onSettled({ type: 'timeout' });
        return;
      }
      timer = setTimeout(() => void tick(), ASYNC_POLL_INTERVAL_MS);
    };

    timer = setTimeout(() => void tick(), ASYNC_POLL_GRACE_MS);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }

  /**
   * Async-via-WebSocket dispatch for `image_generate`.
   *
   * Steps:
   *   1. POST the kickoff. The server returns `{ requestId, status:
   *      'pending' }` in ~10 ms.
   *   2. Subscribe to the shared `FiasWebSocket` for that requestId.
   *      `getWebSocket` lazily fetches its own Cognito-gated ticket
   *      via `authApi.getWebSocketTicket()`, so no extra auth wiring.
   *   3. Register `onImageComplete` + `onError` callbacks filtered by
   *      requestId. Broadcasts for any other in-flight request the
   *      user owns no-op.
   *   4. On match: resolve with the final `ImageInvokeResult` payload
   *      shape (strip broadcast-only `type` and `requestId` fields).
   *      The outer `handleMessage` in turn posts this into the iframe
   *      so the SDK's `await fias.images.generate(...)` resolves
   *      byte-for-byte the same as the pre-refactor synchronous path.
   *   5. Poll fallback: an escalating `image-status` poll (quiet for the
   *      first ~15 s, then every ~5 s up to a 180 s ceiling) recovers a
   *      missed broadcast in seconds. `completed` → resolve; `failed` →
   *      reject; exhausted / `not_found` → reject with
   *      `image_generate_timeout`.
   *   6. Cleanup runs on EVERY terminal path (success / error /
   *      timeout / host destroy) — see `pendingImageRequests`
   *      comment + the cleanup() function below.
   *
   * If the server responds with anything other than `status: 'pending'`
   * (legacy code paths, future inline responses), this method falls
   * through to returning the body as-is.
   */
  private async handleAsyncImageGenerate(msg: BridgeMessage): Promise<unknown> {
    const initialResponse = (await this.proxyToBackend(msg)) as {
      requestId?: string;
      status?: string;
      [key: string]: unknown;
    };
    if (initialResponse?.status !== 'pending' || typeof initialResponse.requestId !== 'string') {
      return initialResponse;
    }
    const requestId = initialResponse.requestId;
    const ws = getWebSocket(WEBSOCKET_ENDPOINT);

    return new Promise<unknown>((resolve, reject) => {
      let settled = false;
      const finish = (cb: () => void): void => {
        if (settled) return;
        settled = true;
        const entry = this.pendingImageRequests.get(requestId);
        if (entry) {
          entry.cleanup();
          this.pendingImageRequests.delete(requestId);
        }
        cb();
      };

      const unsubComplete = ws.onImageComplete((data) => {
        if (data.requestId !== requestId) return;
        // Reconstruct the pre-refactor `ImageInvokeResult` shape
        // byte-for-byte. The broadcast carries broadcast-only metadata
        // (`type`, `requestId`) that the SDK contract did not include
        // — stripping them keeps the iframe-bound payload identical to
        // what the synchronous HTTP response returned pre-refactor.
        const payload: Record<string, unknown> = {
          imageUrl: data.imageUrl,
          imageRef: data.imageRef,
          fileId: data.fileId,
          revisedPrompt: data.revisedPrompt,
          provider: data.provider,
          model: data.model,
          costCents: data.costCents,
        };
        if (data.deprecation) payload.deprecation = data.deprecation;
        finish(() => resolve(payload));
      });

      const unsubError = ws.onError((code, message, errRequestId) => {
        // Only fail THIS entry. Generic connection errors (no requestId
        // — e.g. CONNECTION_FAILED after max reconnect attempts) reach
        // every listener; per-request errors broadcast by the server
        // (e.g. image_generate failures) carry the originating
        // requestId via the extended `ErrorCallback` signature. Both
        // cases land here; the filter below routes them correctly:
        //   - requestId === ours → reject this pending image entry
        //   - requestId undefined → treat as a connection-level error
        //     and reject every pending entry (callers can retry)
        //   - requestId for a different request → ignore
        if (errRequestId !== undefined && errRequestId !== requestId) return;
        finish(() => reject(new Error(`${code}: ${message}`)));
      });

      // Escalating poll fallback. The WS `image_complete` (live or replayed
      // on subscribe) normally settles first and cancels this; if the
      // broadcast was missed, the poll recovers the durable result in seconds.
      const cancelPoll = this.startPollFallback(
        () =>
          this.pollImageStatus(requestId).then((s) =>
            s.kind === 'completed' ? { kind: 'completed' as const, value: s.payload } : s,
          ),
        (outcome) => {
          if (outcome.type === 'completed') {
            finish(() => resolve(outcome.value));
          } else if (outcome.type === 'failed') {
            finish(() => reject(outcome.error));
          } else {
            finish(() => reject(new Error('image_generate_timeout')));
          }
        },
      );

      const cleanup = (): void => {
        unsubComplete();
        unsubError();
        cancelPoll();
        // Unsubscribe THIS request only. Parameterless `unsubscribe()`
        // would clear every subscription on the shared singleton —
        // including a sibling internal-arche generation in another tab
        // and any other pending plugin image_generate. The targeted
        // form leaves those untouched.
        ws.unsubscribe(requestId);
      };

      this.pendingImageRequests.set(requestId, { resolve, reject, cleanup });

      // Best-effort WS subscribe for the fast path, AFTER the poll fallback is
      // already armed. Detached on purpose: a slow or wedged connect (e.g. the
      // socket is mid-reconnect after a page reload) must NEVER block the poll
      // — that's the durable safety net. See subscribeForAsyncResult.
      void this.subscribeForAsyncResult(ws, requestId);
    });
  }

  /**
   * Best-effort WebSocket subscribe for an async (`image_generate` /
   * `surface_invoke`) result. Runs DETACHED from the request promise so the
   * escalating poll fallback is never gated behind the WebSocket: if the
   * socket is mid-reconnect, the connect hangs, or the ticket fetch fails,
   * the poll still recovers the result from the server's durable
   * `request_state`. On success this is the fast path — the live
   * `image_complete` / `surface_complete` broadcast, or the replay the WS
   * service sends when this (possibly late) subscribe lands after completion,
   * resolves the pending request before the poll's grace window elapses.
   */
  private async subscribeForAsyncResult(
    ws: ReturnType<typeof getWebSocket>,
    requestId: string,
  ): Promise<void> {
    try {
      if (ws.getState() !== 'connected') {
        await ws.connect(() => authApi.getWebSocketTicket());
      }
      // The connect above can await a slow / mid-reconnect socket. If the
      // request already settled (success / error / timeout) or the host was
      // destroyed while we waited, its cleanup() has already run
      // ws.unsubscribe(requestId) — re-subscribing now would orphan a
      // subscription on the process-wide singleton that nothing ever removes
      // (and that re-sends on every reconnect). Only subscribe if the request
      // is still pending. requestIds are unique across both maps.
      if (
        !this.pendingImageRequests.has(requestId) &&
        !this.pendingSurfaceRequests.has(requestId)
      ) {
        return;
      }
      ws.subscribe(requestId);
    } catch {
      // Non-fatal: the poll fallback owns delivery when the WS can't.
    }
  }

  /**
   * Polls `GET /v1/plugins/image-status/:requestId` once. Returns a
   * discriminated union mirroring the server-side 4-state contract.
   * Called only from the 180 s safety-timer path; the happy path
   * resolves via WebSocket.
   */
  private async pollImageStatus(
    requestId: string,
  ): Promise<
    | { kind: 'pending' }
    | { kind: 'completed'; payload: unknown }
    | { kind: 'failed'; code: string; message: string }
    | { kind: 'not_found' }
  > {
    try {
      // Authenticate with the HOST's browser SESSION (the `fias_session`
      // cookie), NOT `getAuthToken()`. `getAuthToken` returns the plugin
      // BRIDGE capability token (for `/bridge/:archId`), but `/plugins/
      // image-status` is `requireAuth` (session) — sending the bridge token
      // there 401s, which silently degrades every poll to `pending` and is
      // why the fallback never recovered a missed broadcast. `credentials:
      // 'include'` matches how the rest of the web app calls session routes.
      const resp = await fetch(
        `${this.options.bridgeApiUrl}/plugins/image-status/${encodeURIComponent(requestId)}`,
        { credentials: 'include' },
      );
      if (resp.status === 404) return { kind: 'not_found' };
      const body = (await resp.json()) as {
        status?: string;
        payload?: unknown;
        error?: { code?: string; message?: string };
      };
      if (body.status === 'completed') return { kind: 'completed', payload: body.payload };
      if (body.status === 'failed') {
        return {
          kind: 'failed',
          code: body.error?.code ?? 'GENERATION_FAILED',
          message: body.error?.message ?? 'Image generation failed',
        };
      }
      return { kind: 'pending' };
    } catch {
      return { kind: 'pending' };
    }
  }

  /**
   * Async-via-WebSocket dispatch for `surface_invoke`. Generalizes
   * `handleAsyncImageGenerate` to any surface declaring `asyncDispatch`.
   *
   * Steps mirror the image path:
   *   1. POST the kickoff. An async surface returns `{ requestId, status:
   *      'pending' }`; a synchronous surface returns its full result —
   *      detected here and passed straight through (no WS wait).
   *   2. Subscribe to the shared FiasWebSocket for that requestId.
   *   3. Resolve on the `surface_complete` broadcast for THIS requestId
   *      with the opaque `result` (each surface's own `resultSchema`).
   *   4. 180s safety timer → single `surface-status` poll fallback.
   *   5. Cleanup on every terminal path (success/error/timeout/destroy).
   */
  private async handleAsyncSurfaceInvoke(msg: BridgeMessage): Promise<unknown> {
    const initialResponse = (await this.proxyToBackend(msg)) as {
      requestId?: string;
      status?: string;
      [key: string]: unknown;
    };
    if (initialResponse?.status !== 'pending' || typeof initialResponse.requestId !== 'string') {
      return initialResponse;
    }
    const requestId = initialResponse.requestId;
    const ws = getWebSocket(WEBSOCKET_ENDPOINT);

    return new Promise<unknown>((resolve, reject) => {
      let settled = false;
      const finish = (cb: () => void): void => {
        if (settled) return;
        settled = true;
        const entry = this.pendingSurfaceRequests.get(requestId);
        if (entry) {
          entry.cleanup();
          this.pendingSurfaceRequests.delete(requestId);
        }
        cb();
      };

      const unsubComplete = ws.onSurfaceComplete((data) => {
        if (data.requestId !== requestId) return;
        finish(() => resolve(data.result));
      });

      const unsubError = ws.onError((code, message, errRequestId) => {
        // Per-request errors carry the originating requestId; connection-
        // level errors (no requestId) fail every pending entry. Ignore
        // errors tagged for a different request.
        if (errRequestId !== undefined && errRequestId !== requestId) return;
        finish(() => reject(new Error(`${code}: ${message}`)));
      });

      // Escalating poll fallback (same contract as the image path). The WS
      // `surface_complete` (live or replayed on subscribe) normally settles
      // first and cancels this.
      const cancelPoll = this.startPollFallback(
        () =>
          this.pollSurfaceStatus(requestId).then((s) =>
            s.kind === 'completed' ? { kind: 'completed' as const, value: s.result } : s,
          ),
        (outcome) => {
          if (outcome.type === 'completed') {
            finish(() => resolve(outcome.value));
          } else if (outcome.type === 'failed') {
            finish(() => reject(outcome.error));
          } else {
            finish(() => reject(new Error('surface_invoke_timeout')));
          }
        },
      );

      const cleanup = (): void => {
        unsubComplete();
        unsubError();
        cancelPoll();
        ws.unsubscribe(requestId);
      };

      this.pendingSurfaceRequests.set(requestId, { resolve, reject, cleanup });

      // Best-effort WS subscribe, detached so it can't block the poll. See
      // subscribeForAsyncResult (shared with the image path).
      void this.subscribeForAsyncResult(ws, requestId);
    });
  }

  /**
   * Polls `GET /v1/plugins/surface-status/:requestId` once. Mirrors
   * `pollImageStatus`; called only from the 180s safety-timer path.
   */
  private async pollSurfaceStatus(
    requestId: string,
  ): Promise<
    | { kind: 'pending' }
    | { kind: 'completed'; result: unknown }
    | { kind: 'failed'; code: string; message: string }
    | { kind: 'not_found' }
  > {
    try {
      // Session-cookie auth (see pollImageStatus): `/plugins/surface-status`
      // is `requireAuth`, so the host's browser session — not the bridge
      // capability token from `getAuthToken()` — is what authenticates here.
      const resp = await fetch(
        `${this.options.bridgeApiUrl}/plugins/surface-status/${encodeURIComponent(requestId)}`,
        { credentials: 'include' },
      );
      if (resp.status === 404) return { kind: 'not_found' };
      const body = (await resp.json()) as {
        status?: string;
        result?: unknown;
        error?: { code?: string; message?: string };
      };
      if (body.status === 'completed') return { kind: 'completed', result: body.result };
      if (body.status === 'failed') {
        return {
          kind: 'failed',
          code: body.error?.code ?? 'SURFACE_INVOCATION_FAILED',
          message: body.error?.message ?? 'Surface invocation failed',
        };
      }
      return { kind: 'pending' };
    } catch {
      return { kind: 'pending' };
    }
  }

  private proxyToBackend(msg: BridgeMessage): Promise<unknown> {
    return this.callBackendOp(msg.type, msg.payload);
  }

  /**
   * Bridge endpoint for an op. Image-bearing `entity_invoke` goes to the
   * dedicated `/bridge-vision` route (a higher, isolated body limit for inline
   * image bytes); everything else uses the 256 KB `/bridge` route.
   */
  private bridgeEndpoint(type: string, payload: unknown): string {
    const images = (payload as { images?: unknown })?.images;
    const hasImages = type === 'entity_invoke' && Array.isArray(images) && images.length > 0;
    const path = hasImages ? 'bridge-vision' : 'bridge';
    return `${this.options.bridgeApiUrl}/plugins/${path}/${this.options.archId}`;
  }

  /** POST a single bridge op to the backend from the (unrestricted) host. */
  private async callBackendOp(type: string, payload: unknown): Promise<unknown> {
    const token = await this.options.getAuthToken();
    const response = await fetch(this.bridgeEndpoint(type, payload), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ type, payload }),
      },
    );

    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: 'Bridge call failed' }));
      throw new Error(err.error?.message ?? 'Bridge call failed');
    }

    return response.json();
  }

  /**
   * Host-orchestrated binary upload (see the `vault_documents_upload` case).
   * Runs init → presigned PUT → finalize in the host context so the iframe's
   * `connect-src 'none'` CSP never blocks it. The presigned `uploadUrl` is
   * returned by the backend (init), so the iframe can't redirect the PUT
   * (no SSRF). Returns the finalize result (`{ documentId, status }`).
   */
  private async handleVaultUpload(payload: unknown): Promise<unknown> {
    const p = (payload ?? {}) as {
      name?: unknown;
      mimeType?: unknown;
      bytes?: unknown; // base64
      tags?: unknown;
      sensitivity?: unknown;
      folderPath?: unknown;
      replacesDocumentId?: unknown;
    };
    if (
      typeof p.name !== 'string' ||
      typeof p.mimeType !== 'string' ||
      typeof p.bytes !== 'string'
    ) {
      throw new Error('vault_documents_upload: name, mimeType, and bytes (base64) are required');
    }
    const u8 = base64ToBytes(p.bytes);
    const digest = await crypto.subtle.digest('SHA-256', u8);
    const checksumSha256 = bytesToHex(new Uint8Array(digest));

    const init = (await this.callBackendOp('vault_documents_upload_init', {
      name: p.name,
      mimeType: p.mimeType,
      sizeBytes: u8.byteLength,
      checksumSha256,
      tags: p.tags,
      sensitivity: p.sensitivity,
      folderPath: p.folderPath,
      replacesDocumentId: p.replacesDocumentId,
    })) as { documentId: string; uploadUrl: string; requiredHeaders: Record<string, string> };

    // Browsers forbid setting Content-Length explicitly; strip it.
    const headers = Object.fromEntries(
      Object.entries(init.requiredHeaders ?? {}).filter(
        ([k]) => k.toLowerCase() !== 'content-length',
      ),
    );
    const putResp = await fetch(init.uploadUrl, { method: 'PUT', body: u8, headers });
    if (!putResp.ok) {
      const text = await putResp.text().catch(() => '');
      throw new Error(`Vault upload PUT failed (${putResp.status}): ${text || putResp.statusText}`);
    }

    return this.callBackendOp('vault_documents_upload_finalize', {
      documentId: init.documentId,
      checksumSha256,
    });
  }

  private checkRateLimit(type: string, payload: unknown): void {
    // Burst detection first (catches runaway loops in <5s)
    this.checkBurst(type, payload);

    const limit = RATE_LIMITS[type];
    if (!limit) return;

    const now = Date.now();
    const bucket = this.rateLimits.get(type);

    if (!bucket || now - bucket.windowStart > 60_000) {
      this.rateLimits.set(type, { count: 1, windowStart: now });
      return;
    }

    if (bucket.count >= limit.maxPerMinute) {
      const target = describeBridgeTarget(type, payload);
      const targetSuffix = target ? ` (${target})` : '';
      throw new Error(
        `Rate limit exceeded for ${type}${targetSuffix}: max ${limit.maxPerMinute}/minute`,
      );
    }

    bucket.count++;
  }

  private checkBurst(type: string, payload: unknown): void {
    const now = Date.now();
    const bucket = this.burstBuckets.get(type);
    const target = describeBridgeTarget(type, payload);
    const targetSuffix = target ? ` (${target})` : '';

    // If currently in cooldown, block silently (onLog already fired on first breach)
    if (bucket?.blockedUntil && now < bucket.blockedUntil) {
      throw new BridgeBurstError(
        `Bridge call "${type}"${targetSuffix} temporarily blocked — rapid-fire loop detected. ` +
          `Check useEffect dependency arrays, or debounce writes if this is a usePersistentState key being updated at high frequency.`,
      );
    }

    // Reset expired burst windows
    if (!bucket || now - bucket.windowStart > BURST_WINDOW_MS) {
      this.burstBuckets.set(type, { count: 1, windowStart: now });
      return;
    }

    bucket.count++;

    if (bucket.count > BURST_THRESHOLD) {
      bucket.blockedUntil = now + BURST_COOLDOWN_MS;
      const msg =
        `Runaway loop detected: "${type}"${targetSuffix} called ${bucket.count} times in ${BURST_WINDOW_MS / 1000}s. ` +
        `Calls blocked for ${BURST_COOLDOWN_MS / 1000}s. ` +
        `Check useEffect dependency arrays, or debounce writes if this is a usePersistentState key being updated at high frequency.`;
      this.options.onLog?.('error', msg);
      throw new BridgeBurstError(
        `Bridge call "${type}"${targetSuffix} temporarily blocked — rapid-fire loop detected.`,
      );
    }
  }

  private sendToPlugin(message: BridgeMessage): void {
    // targetOrigin '*' is required because sandboxed iframes without allow-same-origin
    // have an opaque (null) origin. Security is enforced by checking event.source
    // against iframe.contentWindow on incoming messages (line 113).
    this.options.iframe.contentWindow?.postMessage(message, '*');
  }

  private sendResponse(messageId: string, payload: unknown): void {
    this.sendToPlugin({
      type: 'response' as string,
      messageId,
      payload,
    });
  }

  private sendError(messageId: string, error: string): void {
    // See sendToPlugin() for why '*' is used as targetOrigin.
    this.options.iframe.contentWindow?.postMessage(
      {
        type: 'response',
        messageId,
        payload: null,
        error,
      },
      '*',
    );
  }
}

// ── Binary helpers for the host-orchestrated vault upload ────────────────────

function base64ToBytes(b64: string): Uint8Array<ArrayBuffer> {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

function bytesToHex(bytes: Uint8Array): string {
  let out = '';
  for (let i = 0; i < bytes.length; i++) out += bytes[i].toString(16).padStart(2, '0');
  return out;
}
